import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Grid,
    Paper,
    Skeleton,
    TextField,
    Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { LinearProgress } from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pt-br';
import { useAgreementsController } from "./context/agreements-controller";
import { useAgreementsState } from "./context/agreements-context";
import { IListAgreementsRequest } from "../../model/base/request/list-agreements-request";
import PageHeader from "../components/shared/page-header";
import { useNavigate } from "../../hooks/useNavigate";

export function AgreementsComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadMoreData, setLoadMoreData] = useState(false);
    const { loadData } = useAgreementsController();
    const { agreements } = useAgreementsState();
    const [paramsRequest, setParamsRequest] = useState<IListAgreementsRequest>({ page: 0, pageSize: 10 } as IListAgreementsRequest);
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const searchData = async () => {
            setLoadMoreData(true);
            await loadData(paramsRequest);
            setLoadMoreData(false);
            setInitialLoading(false);
        };
        searchData();
    }, [paramsRequest]);

    const handleClearFilters = () => {
        setParamsRequest({ page: 0, pageSize: 10 } as IListAgreementsRequest);
        setStartDate(null);
        setEndDate(null);
    };

    const handleStartDateChange = (newValue: Dayjs | null) => {
        setStartDate(newValue);
        setParamsRequest({
            ...paramsRequest,
            page: 0,
            startDate: newValue ? newValue.format('YYYY-MM-DD') : undefined
        });
    };

    const handleEndDateChange = (newValue: Dayjs | null) => {
        setEndDate(newValue);
        setParamsRequest({
            ...paramsRequest,
            page: 0,
            endDate: newValue ? newValue.format('YYYY-MM-DD') : undefined
        });
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    const columns: GridColDef[] = [
        {
            field: 'superiorBody',
            headerName: 'Órgão Superior',
            flex: 2,
        },
        {
            field: 'subordinateBody',
            headerName: 'Órgão Subordinado',
            flex: 2,
        },
        {
            field: 'managementUnit',
            headerName: 'Unidade Gestora',
            flex: 2,
        },
        {
            field: 'period',
            headerName: 'Período',
            flex: 1.5,
            renderCell: (params) => {
                const startDate = params.row.startDate ? dayjs(params.row.startDate).format('DD/MM/YYYY') : '-';
                const endDate = params.row.endDate ? dayjs(params.row.endDate).format('DD/MM/YYYY') : '-';
                return `${startDate} - ${endDate}`;
            },
        },
        {
            field: 'transferAmount',
            headerName: 'Valor Repasse',
            flex: 1,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => formatCurrency(params.value),
        },
        {
            field: 'counterpartAmount',
            headerName: 'Valor Contrapartida',
            flex: 1,
            align: 'right',
            headerAlign: 'right',
            renderCell: (params) => formatCurrency(params.value),
        },
        {
            field: 'actions',
            headerName: 'Ações',
            width: 150,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            renderCell: (params) => (
                <Button
                    variant="text"
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate(`/convenios-repasses/${params.row.id}`)}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 500
                    }}
                >
                    Ver detalhes
                </Button>
            ),
        },
    ];

    return (
        <Box>
            <PageHeader
                title="Convênios e Repasses"
                subtitle="Consulte os convênios e repasses estabelecidos pelo município, promovendo transparência sobre os recursos recebidos e suas aplicações."
                breadcrumbs={[{ label: 'Convênios e Repasses' }]}
            />

            {initialLoading ? (
                <Skeleton variant="rounded" width="100%" height={500} sx={{ borderRadius: 2 }} />
            ) : (
                <React.Fragment>
                    <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={6} md={3.5}>
                                    <TextField
                                        fullWidth
                                        label="Buscar"
                                        placeholder="Digite para buscar"
                                        value={paramsRequest.term || ''}
                                        onChange={(e) => {
                                            setParamsRequest({ ...paramsRequest, page: 0, term: e.target.value });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Ano"
                                        placeholder="Digite o ano"
                                        value={paramsRequest.year || ''}
                                        onChange={(e) => {
                                            setParamsRequest({
                                                ...paramsRequest,
                                                page: 0,
                                                year: e.target.value ? Number(e.target.value) : undefined
                                            });
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <DatePicker
                                        label="Data de"
                                        value={startDate}
                                        onChange={handleStartDateChange}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <DatePicker
                                        label="Data até"
                                        value={endDate}
                                        onChange={handleEndDateChange}
                                        format="DD/MM/YYYY"
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2.5}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<ClearIcon />}
                                        onClick={handleClearFilters}
                                        fullWidth
                                        sx={{ height: '56px' }}
                                    >
                                        Limpar Filtros
                                    </Button>
                                </Grid>
                            </Grid>
                        </LocalizationProvider>
                    </Paper>

                        <Paper sx={{ p: 3, minHeight: 500 }} elevation={2}>
                            {loadMoreData ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                                    <LinearProgress sx={{ width: '50%' }} />
                                </Box>
                            ) : agreements?.items?.length === 0 ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                                    <Typography variant="h6" color="text.secondary">
                                        Nenhum convênio encontrado
                                    </Typography>
                                </Box>
                            ) : (
                                <DataGrid
                                    key={JSON.stringify(paramsRequest)}
                                    rows={agreements?.items || []}
                                    rowCount={agreements?.totalRecords || 0}
                                    columns={columns}
                                    getRowId={(row) => row.id}
                                    hideFooterSelectedRowCount
                                    paginationModel={{ page: paramsRequest.page, pageSize: paramsRequest.pageSize } as GridPaginationModel}
                                    onPaginationModelChange={(e) => { setParamsRequest({ ...paramsRequest, page: e.page, pageSize: e.pageSize }) }}
                                    pageSizeOptions={[5]}
                                    paginationMode="server"
                                    slots={{
                                        loadingOverlay: LinearProgress,
                                    }}
                                    loading={loadMoreData}
                                    localeText={{
                                        MuiTablePagination: {
                                            labelDisplayedRows: ({ from, to, count }) =>
                                                `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`,
                                            labelRowsPerPage: 'Linhas por página:',
                                        },
                                        noRowsLabel: 'Nenhum registro encontrado',
                                        noResultsOverlayLabel: 'Nenhum resultado encontrado',
                                    }}
                                />
                            )}
                        </Paper>
                    </React.Fragment>
                )}
        </Box>
    );
}
